import type { AssetTypeType } from "../schemas/AssetSchemas.ts";
import type {
  CalculatorResultExportListRequestType,
  CalculatorResultGetListRequestSchema,
  CalculatorResultGetListRequestType,
} from "../schemas/CalculatorResultsSchemas.ts";
import type { EquipmentType } from "../schemas/EquipmentSchemas.ts";
import type { FacilityType } from "../schemas/FacilitySchemas.ts";
import type { ReportV2Type } from "../schemas/ReportSchemas.ts";
import { fetchAndCollate, getFilterObject } from "../util/index.ts";
import type { GetListResponseType } from "../util/index.ts";
import { restAPI } from "./api.ts";
import { SavedFiltersAdapter } from "./composable/index.ts";

export type CalculatorResultType = {
  measurement: CalculatorResultMeasurementType;
  // the following fields are present depending on the query made
  year_month?: string;
  entity_id?: string;
  entity_type?: AssetTypeType;
  estimation_method_id?: string;
  estimation_method_name?: string;
  measurement_type?: string;
  equipment?: EquipmentType;
  "equipment.id"?: string;
  "equipment.name"?: string;
  "equipment.method"?: string;
  "equipment.status"?: string;
  "equipment.type.id"?: string;
  "equipment.type.name"?: string;
  facility?: FacilityType;
  "equipment.facility.id"?: string;
  "equipment.facility.name"?: string;
  "equipment.facility.status"?: string;
  "estimation_method.analytics_calculator_id"?: string;
  /** the ID of the reporting group */
  reporting_group: string;
};

export type CalculatorResultMeasurementType = Record<string, number>;

export const CalculatorResultsDomain = {
  /**
   * Search and aggregate calculator results
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/calculator_results/calculator_results
   */
  getList: ({
    filters,
    page,
    pageSize,
    pageDisableTotal,
    sortBy,
    sortDirection,
    groupBy,
    meta,
    ...restProps
  }: CalculatorResultGetListRequestType): Promise<
    GetListResponseType<CalculatorResultType>
  > => {
    const formattedFilters = getFilterObject(filters);

    return fetchAndCollate(
      ({ page, pageSize }) =>
        restAPI.nodeAPI.POST<GetListResponseType<CalculatorResultType>>({
          endpoint: "/calculator_results",
          body: {
            ...restProps,
            page,
            page_size: pageSize,
            page_disable_total: pageDisableTotal,
            sort_by: sortBy,
            sort_direction: sortDirection,
            filter: formattedFilters,
            group_by: groupBy,
          },
          ...(meta?.isExcelFile
            ? {
                headers: {
                  Accept:
                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                },
                responseType: "arraybuffer",
              }
            : {}),
        }),
      page,
      pageSize
    );
  },

  /**
   * Export search and aggregation of calculator results
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/calculator_results/export_calculator_results
   */
  export: async ({
    filters,
    sortBy,
    sortDirection,
    groupBy,
  }: CalculatorResultExportListRequestType) => {
    const formattedFilters = getFilterObject(filters);

    return await restAPI.nodeAPI.POST<ReportV2Type>({
      endpoint: "/calculator_results/export",
      body: {
        sort_by: sortBy,
        sort_direction: sortDirection,
        filter: formattedFilters,
        group_by: groupBy,
      },
    });
  },

  /**
   * Calculator Results Saved Filter APIs
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/calculator_results/get_calculator_results_filter
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/calculator_results/post_calculator_results_filter
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/calculator_results/delete_calculator_results_filter
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/calculator_results/put_calculator_results_filter
   */
  savedFilters: SavedFiltersAdapter<
    typeof CalculatorResultGetListRequestSchema.shape.filters
  >({
    endpoint: "/calculator_results/filters",
  }),
};
